/**
 * ASGTRANS's privacy policy and legal notice, as links (design §8.1): before sign-in on the two
 * pre-login screens, which share no layout, and in the user menu (`shell/AppShell.tsx`).
 *
 * Plain links in a new tab, not routes: the pages are static files outside the SPA, and a router
 * link would render the portal's not-found screen. Only approved documents, in the portal's current
 * language.
 */

import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import type { Language } from '../app/i18n.ts'
import { LEGAL_DOC_KEYS, legalPageUrl, type LegalDocKey } from './pages.ts'
import { LEGAL_APPROVED } from './status.ts'

export const LEGAL_LABEL_KEYS: Readonly<Record<LegalDocKey, string>> = {
  privacidad: 'legal.privacy',
  'aviso-legal': 'legal.notice',
}

/** `en-GB` and `en` are English; anything else is Spanish, the portal's own fallback (`app/i18n.ts`). */
export function legalLanguage(language: string | undefined): Language {
  return language?.startsWith('en') ? 'en' : 'es'
}

/** The documents the portal may link: approved ones only, in their fixed order. */
export function linkableLegalDocs(): LegalDocKey[] {
  return LEGAL_DOC_KEYS.filter((key) => LEGAL_APPROVED[key])
}

export function LegalLinks({ className }: { className: string }) {
  const { t, i18n } = useTranslation()
  const docs = linkableLegalDocs()
  if (docs.length === 0) return null
  const language = legalLanguage(i18n.language)
  return (
    <p className={className}>
      {docs.map((key, index) => (
        <Fragment key={key}>
          {index > 0 ? ' · ' : null}
          <a href={legalPageUrl(key, language)} target="_blank" rel="noopener">
            {t(LEGAL_LABEL_KEYS[key])}
          </a>
        </Fragment>
      ))}
    </p>
  )
}
